import React, { useState } from "react";
import { getDisplayPlatforms } from "../utils/networkNames";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { Share2, Heart, CalendarDays } from "lucide-react";
import Typography from "./ui/Typography";
import Button from "./ui/Button";
import Chip from "./ui/Chip";
import RatingTooltipBadge from "./ui/RatingTooltipBadge";
import SeasonBadge from "./ui/SeasonBadge";
import PosterPlaceholder from "./ui/PosterPlaceholder";
import DiscoveryCue from "./DiscoveryCue";
import ShowsLikeLink from "./ShowsLikeLink";
import { cn } from "../utils/classNames";
import { formatReleaseDate } from "../utils/formatReleaseDate";
import { shareShow } from "../utils/share";
import { handleInternalLinkClick } from "../utils/navigation";
import { getContentRating, normalizeSeasonNumber } from "../utils/showFields";
import { getPrimaryRating } from "../utils/showRatings";
import { getWatchlistToggleLabel } from "../utils/watchlist";

const CompactCard = React.forwardRef(
    ({
        release,
        className,
        priority = false,
        highlighted = false,
        isInWatchlist,
        onToggleWatchlist,
        ...props
    }, ref) => {
        const [isHovered, setIsHovered] = useState(false);
        const [heartPulse, setHeartPulse] = useState(false);
        const inWatchlist = isInWatchlist?.(release);
        const contentRating = getContentRating(release);
        const seasonNumber = normalizeSeasonNumber(release.seasonNumber);

        // Get primary network
        const primaryNetwork = getDisplayPlatforms(release);

        // Get display genres (limit to first 2)
        const displayGenres = release.genres?.slice(0, 2) || [];

        // Use poster as backdrop fallback
        const posterImage = release.poster;
        const showPath = release.slug ? `/show/${release.slug}` : null;
        const hoverShadow = "var(--shadow-card-dense-hover)";

        return (
            <motion.div
                ref={ref}
                id={`show-${release.id}`}
                className={cn(
                    "group compact-release-card relative isolate grid h-full overflow-hidden rounded-xl border border-accent-gold/60 bg-card shadow-card",
                    "transition-all duration-200",
                    highlighted && "random-pick-highlight",
                    className
                )}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                initial={{ x: 0, y: 0, boxShadow: "none" }}
                animate={
                    isHovered
                        ? { x: -3, y: -3, boxShadow: hoverShadow }
                        : { x: 0, y: 0, boxShadow: "var(--shadow-card-base)" }
                }
                transition={{
                    duration: 0.25,
                    ease: [0.0, 0.0, 0.2, 1],
                    boxShadow: { duration: 0.25, ease: [0.0, 0.0, 0.2, 1] },
                }}
                {...props}
            >
                {showPath && (
                    <a
                        href={showPath}
                        onClick={(event) => handleInternalLinkClick(event, showPath)}
                        className="absolute inset-0 z-30"
                        aria-label={`View details for ${release.title}`}
                    />
                )}

                <div className="relative min-h-full overflow-hidden bg-muted">
                    {posterImage ? (
                        <div className="absolute inset-0">
                            <img
                                src={posterImage}
                                alt={`${release.title} poster`}
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                                loading="lazy"
                                fetchPriority={priority ? "high" : "auto"}
                            />
                            <div className="absolute inset-0 bg-linear-to-t from-black/50 via-transparent to-black/12" />
                        </div>
                    ) : (
                        <div className="absolute inset-0">
                            <PosterPlaceholder
                                showId={release.id}
                                className="w-full h-full bg-poster-placeholder-gradient"
                            />
                        </div>
                    )}

                    {getPrimaryRating(release) !== null && (
                        <div className="pointer-events-auto absolute left-2 top-2 z-50">
                            <RatingTooltipBadge show={release} badgeClassName="shadow-badge" />
                        </div>
                    )}
                </div>

                <div className="pointer-events-none relative z-40 flex min-w-0 flex-col gap-3 p-3.5 sm:p-4">
                    <div className="min-w-0 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                            <Typography
                                variant="h3"
                                className="title-link compact-release-card__title line-clamp-2"
                            >
                                {release.title}
                            </Typography>
                            <div className="pointer-events-auto relative z-50 flex shrink-0 items-center gap-1">
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onToggleWatchlist?.(release);
                                        setHeartPulse(true);
                                        setTimeout(() => setHeartPulse(false), 300);
                                    }}
                                    className={cn(
                                        "sp-card-icon-action",
                                        inWatchlist && "sp-card-icon-action--active"
                                    )}
                                    aria-label={getWatchlistToggleLabel(release.title)}
                                    aria-pressed={Boolean(inWatchlist)}
                                >
                                    <Heart className={cn("h-4 w-4", inWatchlist && "fill-current text-primary", heartPulse && "watchlist-pulse")} aria-hidden="true" />
                                </Button>
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        shareShow(release);
                                    }}
                                    className="sp-card-icon-action"
                                    aria-label={`Share ${release.title}`}
                                >
                                    <Share2 className="h-4 w-4" aria-hidden="true" />
                                </Button>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5">
                            <SeasonBadge seasonNumber={seasonNumber} part={release.seasonPart} variant="compact" />
                            {contentRating && (
                                <Chip variant="outline" size="badge" className="text-label">
                                    {contentRating}
                                </Chip>
                            )}
                        </div>

                        <div className="space-y-1.5">
                            <div className="sp-card-date-meta">
                                <CalendarDays className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                                <span>{formatReleaseDate(release.releaseDate)}</span>
                            </div>
                            <Typography variant="caption" className="line-clamp-1 text-muted-foreground">
                                {[primaryNetwork, ...displayGenres].filter(Boolean).join(" / ")}
                            </Typography>
                        </div>

                        <DiscoveryCue
                            cue={release.discoveryCue}
                            currentSlug={release.slug || ""}
                            currentTitle={release.title || ""}
                            surface="compact_card"
                            compact
                            className="pointer-events-auto relative z-50"
                        />
                        <ShowsLikeLink
                            show={release}
                            surface="compact_card"
                            className="pointer-events-auto relative z-50"
                        />
                    </div>

                </div>
                {showPath && (
                    <span
                        className="pointer-events-none absolute inset-0 z-0 rounded-xl ring-0 ring-primary/0 transition group-hover:ring-2 group-hover:ring-primary/35"
                        aria-hidden="true"
                    />
                )}
            </motion.div>
        );
    }
);

CompactCard.displayName = "CompactCard";

CompactCard.propTypes = {
    release: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
        title: PropTypes.string.isRequired,
        rating: PropTypes.number,
        poster: PropTypes.string,
        seasonNumber: PropTypes.number,
        seasonPart: PropTypes.string,
        releaseDate: PropTypes.string.isRequired,
        sourceName: PropTypes.string,
        genres: PropTypes.arrayOf(PropTypes.string),
        networks: PropTypes.arrayOf(PropTypes.string),
        plotOverview: PropTypes.string,
        contentRating: PropTypes.string,
        trailers: PropTypes.arrayOf(PropTypes.shape({
            url: PropTypes.string,
            name: PropTypes.string,
        })),
        detailsUrl: PropTypes.string,
        slug: PropTypes.string,
        hasShowsLikePage: PropTypes.bool,
        nextWatch: PropTypes.shape({
            slug: PropTypes.string,
            title: PropTypes.string,
            bridge: PropTypes.string,
        }),
        discoveryCue: PropTypes.shape({
            type: PropTypes.oneOf(["inbound", "taste"]),
            label: PropTypes.string,
            anchor: PropTypes.shape({
                slug: PropTypes.string,
                title: PropTypes.string,
            }),
            reasons: PropTypes.arrayOf(PropTypes.string),
        }),
    }).isRequired,
    className: PropTypes.string,
    priority: PropTypes.bool,
    highlighted: PropTypes.bool,
    isInWatchlist: PropTypes.func,
    onToggleWatchlist: PropTypes.func,
};

export default CompactCard;
